import { type ReactNode, useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  LayoutDashboard,
  FileText,
  CreditCard,
  ArrowLeftRight,
  Clock,
  FileSpreadsheet,
  Wallet,
  Menu,
  X,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import type { CurrentUser, Flash } from '@/types'

const navItems = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Purchase Orders', href: '/purchase_orders', icon: FileText },
  { label: 'Payments', href: '/payments', icon: CreditCard },
  { label: 'Adjustments', href: '/adjustments', icon: ArrowLeftRight },
  { label: 'History', href: '/history', icon: Clock },
  { label: 'Statement', href: '/statement', icon: FileSpreadsheet },
]

function NavLink({ item, currentPath }: { item: typeof navItems[0]; currentPath: string }) {
  const isActive =
    item.href === '/'
      ? currentPath === '/'
      : currentPath.startsWith(item.href)

  return (
    <Link
      href={item.href}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-primary/10 text-primary shadow-sm shadow-primary/5'
          : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
      )}
    >
      <item.icon className={cn('size-4 shrink-0', isActive && 'text-primary')} />
      <span>{item.label}</span>
      {isActive && (
        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
      )}
    </Link>
  )
}

function FlashMessages() {
  const { flash } = usePage<{ flash: Flash }>().props
  const [visible, setVisible] = useState<{ type: 'notice' | 'alert'; message: string } | null>(null)

  useEffect(() => {
    if (flash?.notice) {
      setVisible({ type: 'notice', message: flash.notice })
    } else if (flash?.alert) {
      setVisible({ type: 'alert', message: flash.alert })
    }
  }, [flash])

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setVisible(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [visible])

  if (!visible) return null

  return (
    <div className="fixed top-4 right-4 z-[100] animate-in fade-in slide-in-from-top-2 duration-300">
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium shadow-lg backdrop-blur-sm',
          visible.type === 'notice'
            ? 'border-positive/20 bg-positive/10 text-positive'
            : 'border-destructive/20 bg-destructive/10 text-destructive'
        )}
      >
        <span>{visible.message}</span>
        <button
          onClick={() => setVisible(null)}
          className="ml-2 opacity-60 hover:opacity-100 transition-opacity"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  )
}

function Sidebar() {
  const { url: currentPath, props } = usePage<{ current_user: CurrentUser | null }>()
  const currentUser = props.current_user

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2.5 px-4 py-6">
        <div className="flex size-8 items-center justify-center rounded-lg bg-primary/15">
          <Wallet className="size-4 text-primary" />
        </div>
        <span className="text-base font-semibold tracking-tight text-foreground">
          Open Tab
        </span>
      </div>

      <nav className="flex flex-1 flex-col gap-1 px-3">
        {navItems.map((item) => (
          <NavLink key={item.href} item={item} currentPath={currentPath} />
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-4 py-4">
        {currentUser ? (
          <div className="flex items-center justify-between">
            <p className="truncate text-xs text-sidebar-muted">{currentUser.email}</p>
            <Link href="/logout" method="delete" as="button" className="text-sidebar-muted hover:text-foreground transition-colors">
              <LogOut className="size-3.5" />
            </Link>
          </div>
        ) : (
          <p className="text-xs text-sidebar-muted">Balance Ledger</p>
        )}
      </div>
    </div>
  )
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="flex min-h-screen bg-background">
      <FlashMessages />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 border-r border-sidebar-border bg-sidebar">
        <Sidebar />
      </aside>

      {/* Mobile sidebar */}
      <div className="lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="fixed top-3 left-3 z-50 lg:hidden"
            >
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 bg-sidebar">
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation</SheetTitle>
            </SheetHeader>
            <Sidebar />
          </SheetContent>
        </Sheet>
      </div>

      {/* Main content */}
      <main className="flex-1 lg:pl-60">
        <div className="mx-auto max-w-6xl px-4 pt-16 pb-8 sm:px-6 lg:px-8 lg:pt-8">
          {children}
        </div>
      </main>
    </div>
  )
}
